// B6 — the registry's answer for the RS ID under edit: existence, genes, alleles, and — the part
// that changes clinical behaviour — whether the clinic's array can measure it at all. `on_array:
// false` is a pre-save warning, never a block; an outage is `unavailable` (unknown), never
// "resolved" and never treated as absent-from-corpus. The rs renders VERBATIM as typed.
import React, { useEffect, useState } from "react";
import { Form } from "antd";
import { CheckOutlined, LockOutlined, WarningOutlined } from "@ant-design/icons";

import InlineLoading from "../../../../components/shared/InlineLoading";
import { RegistryVariantLookup } from "../../../../types/geneCodes/RegistryTypes";
import { isCompleteRs, lookupVariant } from "../../../../utils/geneCodes/variantEvidence";
import "./wheelBuilder.css";

const SnpRegistryReference: React.FC = () => {
	const form = Form.useFormInstance();
	const watched = Form.useWatch("rs", form);
	const rs = typeof watched === "string" ? watched.trim() : "";
	const complete = isCompleteRs(rs);

	const [lookup, setLookup] = useState<RegistryVariantLookup | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!complete) {
			setLookup(null);
			return undefined;
		}

		let live = true;
		setLoading(true);
		void lookupVariant(rs).then((result) => {
			if (!live) return;
			setLookup(result);
			setLoading(false);
		});

		return () => {
			live = false;
		};
	}, [rs, complete]);

	if (!complete) return null;

	return (
		<div className="mg-node-editor__registry" data-testid="node-editor-registry" data-state={loading || !lookup ? "loading" : lookup.status}>
			<div className="mg-node-editor__gsa-head">
				<span className="mg-node-editor__label">Registry reference</span>
			</div>

			{loading || !lookup ? (
				<InlineLoading message="Checking the registry…" minHeight={48} />
			) : lookup.status === "found" ? (
				<>
					{lookup.doc.on_array === false ? (
						<span className="mg-node-editor__on-array mg-node-editor__on-array--off" data-testid="registry-off-array">
							<WarningOutlined /> {rs} is not measurable on the clinic&rsquo;s array — results will never fill this SNP.
						</span>
					) : lookup.doc.on_array === true ? (
						<span className="mg-node-editor__on-array mg-node-editor__on-array--on" data-testid="registry-on-array">
							<CheckOutlined /> Measurable on the clinic&rsquo;s array{lookup.doc.array_names.length > 0 ? ` (${lookup.doc.array_names.join(", ")})` : ""}.
						</span>
					) : (
						<span className="mg-node-editor__on-array mg-node-editor__on-array--unknown" data-testid="registry-array-unknown">
							Measurability unknown — the registry does not say.
						</span>
					)}
					<div className="mg-node-editor__gsa-grid">
						<div className="mg-node-editor__gsa-cell">
							<span className="mg-node-editor__hint">Genes</span>
							<span className="mg-node-editor__gsa-value" data-testid="registry-genes">
								{lookup.doc.gene_symbols.length > 0 ? lookup.doc.gene_symbols.join(", ") : "—"}
							</span>
						</div>
						<div className="mg-node-editor__gsa-cell">
							<span className="mg-node-editor__hint">Alleles</span>
							<span className="mg-node-editor__gsa-value" data-testid="registry-alleles">
								{lookup.doc.alleles ?? "—"}
							</span>
						</div>
						<div className="mg-node-editor__gsa-cell">
							<span className="mg-node-editor__hint">Evidence</span>
							<span className="mg-node-editor__gsa-value" data-testid="registry-evidence">
								{lookup.doc.assoc_count} assoc · {lookup.doc.pmid_count ?? 0} papers
								{lookup.doc.best_p_value_text ? ` · best p ${lookup.doc.best_p_value_text}` : ""}
							</span>
						</div>
					</div>
				</>
			) : lookup.status === "not_found" ? (
				<span className="mg-node-editor__registry-note mg-node-editor__registry-note--not-found" data-testid="registry-not-found">
					<WarningOutlined /> {rs} is not in the registry corpus — it can still be saved as typed.
				</span>
			) : (
				<span className="mg-node-editor__registry-note mg-node-editor__registry-note--unavailable" data-testid="registry-unavailable">
					<LockOutlined /> Registry unavailable — measurability unknown, nothing verified.
				</span>
			)}
		</div>
	);
};

export default SnpRegistryReference;
